/**
 * Service Categories Hook
 *
 * Optional category picker for a provider's service proposal.
 *
 * Uses the `serviceCategories` root field (orders.ServiceCategory) — the same one
 * getCategoriesWithTemplates queries. Note the older getCategories() in orderApi.ts hits a
 * different root field (`categories`) whose ids are not what the proposal endpoint's
 * category_id expects, so it is not reusable here.
 */

import { useQuery } from '@tanstack/react-query'
import { graphqlQuery } from '@/services/graphql'
import { decodeRelayId } from '@/utils/id'

const SERVICE_CATEGORIES_QUERY = `
  query ServiceCategoriesForProposal {
    serviceCategories(isActive: true) {
      id
      code
      name
      nameEn
      nameAr
      isActive
    }
  }
`

export interface ServiceCategoryOption {
  id: string
  code?: string | null
  name: string
  nameEn?: string | null
  nameAr?: string | null
}

interface ServiceCategoriesResponse {
  serviceCategories: ServiceCategoryOption[] | null
}

export function useServiceCategories(enabled = true) {
  const query = useQuery({
    queryKey: ['serviceCategories'],
    queryFn: async () => {
      const response = await graphqlQuery<ServiceCategoriesResponse>(SERVICE_CATEGORIES_QUERY)
      const categories = response.data?.serviceCategories ?? []
      // REST wants a raw UUID; decodeRelayId is a no-op when the id already is one.
      return categories.map((c) => ({ ...c, id: decodeRelayId(c.id) ?? c.id }))
    },
    enabled,
    staleTime: 1000 * 60 * 30,
  })

  return {
    categories: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
